import { DefaultView } from "@redotech/effect-api-shared/models/support-ticket/default-view";
import { useTranslation } from "@redotech/redo-i18n";
import { useCallback } from "react";

export function useDefaultViewDisplayName(): (view: DefaultView) => string {
  const { t } = useTranslation();
  return useCallback(
    (view: DefaultView) => {
      switch (view) {
        case DefaultView.ALL:
          return t("All tickets", {
            context:
              "support navigation view containing tickets of every status",
          });
        case DefaultView.OPEN:
          return t("Open", {
            context:
              "support navigation view for unresolved tickets, not a verb",
          });
        case DefaultView.ASSIGNED:
          return t("Assigned to me", {
            context:
              "support navigation view for tickets assigned to the current user",
          });
        case DefaultView.MENTIONS:
          return t("Mentions", {
            context:
              "support navigation view for tickets that mention the current user",
          });
        case DefaultView.SENT:
          return t("Sent", {
            context: "support navigation view for tickets with sent messages",
          });
        case DefaultView.DRAFTS:
          return t("Drafts", {
            context:
              "support navigation view for tickets with unsent draft messages",
          });
        case DefaultView.AI_CONVERSATIONS:
          return t("AI conversations", {
            context:
              "support navigation view for tickets assigned to the AI agent",
          });
        case DefaultView.SPAM:
          return t("Spam", {
            context: "support navigation view for tickets marked as spam",
          });
        case DefaultView.ARCHIVED:
          return t("Archived", {
            context: "support navigation view for archived tickets",
          });
      }
    },
    [t],
  );
}

export function useDefaultViewDescription(): (view: DefaultView) => string {
  const { t } = useTranslation();
  return useCallback(
    (view: DefaultView) => {
      switch (view) {
        case DefaultView.ALL:
          return t(
            "Every ticket across all statuses, including spam and AI conversations.",
          );
        case DefaultView.OPEN:
          return t("Open tickets that still need attention.");
        case DefaultView.ASSIGNED:
          return t("Open tickets assigned to you.");
        case DefaultView.MENTIONS:
          return t("Open tickets that mention you.");
        case DefaultView.SENT:
          return t("Tickets with a sent message.");
        case DefaultView.DRAFTS:
          return t("Tickets with unsent drafts.");
        case DefaultView.AI_CONVERSATIONS:
          return t("Tickets assigned to the AI agent.");
        case DefaultView.SPAM:
          return t("Tickets tagged as spam.");
        case DefaultView.ARCHIVED:
          return t("Archived tickets.");
      }
    },
    [t],
  );
}
